import type { NextPage } from "next";
import Head from "next/head";
import { FunctionComponent, useState } from "react";
import Spinner from "../components/Spinner";
import { Tenant } from "../src/types";
import useData from "../src/useData";
import useTenant from "../src/useTenant";
import { HiOutlineExclamation } from "react-icons/hi";
import { Bar, Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import {
  add,
  differenceInDays,
  format,
  isValid,
  parseISO,
  sub,
} from "date-fns";
import MainContent from "@/components/MainContent";

// hardcoded for now. should match the backend.
const MAX_RANGE_DAYS = 60;
const ALL_ENVS = "<all>";

type DailyEnvironmentMetrics = {
  date: string;
  metric_value: number;
  metric_dimensions: { [key: string]: number };
  environment_name: string;
};

type DateRange<T> = {
  start: T;
  end: T;
};

type DataPoint = {
  date: string;
  value: number;
};

// this is the earliest we started logging this data
const earliest = parseISO("2023-03-01");
// there's no stats for today, so the latest we can look is yesterday
const latest = sub(new Date(), { days: 1 });
const formatDate = (date: Date) => format(date, "yyyy-MM-dd");

const Loading: FunctionComponent = () => (
  <div className="flex flex-col items-center justify-center h-full -mt-6">
    <Spinner size={42} />
  </div>
);

const EnvironmentComponent: FunctionComponent<{
  selectedEnv: string;
  tenant: Tenant;
  handleChange: (env: string) => void;
}> = ({ selectedEnv, tenant, handleChange }) => {
  return (
    <select
      style={{
        backgroundColor: "hsl(var(--background))",
        color: "hsl(var(--foreground))",
      }}
      onChange={(evt) => handleChange(evt.target.value)}
      value={selectedEnv}
    >
      <option value={ALL_ENVS}>All Environments</option>
      {tenant.environments.map((env, key) => (
        <option key={key} value={env.pub_id}>
          {env.name}
        </option>
      ))}
    </select>
  );
};

const DateRangeComponent: FunctionComponent<{
  defaultStart: Date;
  defaultEnd: Date;
  handleChange: ({ start, end }: DateRange<string>) => void;
}> = ({ defaultStart, defaultEnd, handleChange }) => {
  const [{ start, end }, setRange] = useState({
    start: formatDate(defaultStart),
    end: formatDate(defaultEnd),
  });

  // color-scheme alone won't dark-fill a native date field; drive bg/text from tokens.
  const dateFieldStyle = {
    border: "none",
    backgroundColor: "hsl(var(--background))",
    color: "hsl(var(--foreground))",
  };

  return (
    <span
      style={{
        border: "1px solid hsl(var(--foreground))",
        display: "inline-block",
      }}
    >
      <input
        style={dateFieldStyle}
        type="date"
        value={start}
        onChange={(e) => {
          const nextRange = { start: e.target.value, end };
          setRange(nextRange);
          handleChange(nextRange);
        }}
        min={formatDate(earliest)}
        max={formatDate(latest)}
      />
      &gt;
      <input
        style={dateFieldStyle}
        type="date"
        value={end}
        onChange={(e) => {
          const nextRange = { start, end: e.target.value };
          setRange(nextRange);
          handleChange(nextRange);
        }}
        min={formatDate(earliest)}
        max={formatDate(latest)}
      />
    </span>
  );
};

const generateLabels = ({ start, end }: DateRange<Date>) => {
  const labels: string[] = [];
  const dayCount = differenceInDays(end, start);
  for (let days = 0; days <= dayCount; days++) {
    let day = add(start, { days });
    labels.push(formatDate(day));
  }
  return labels;
};

const broadcastDates = (labels: string[], metrics: DataPoint[]) => {
  let out = [];
  for (let label of labels) {
    let metric = metrics.find((metric) => metric.date == label);
    out.push(metric?.value);
  }
  return out;
};

const AllEnvironmentsUsageGraph: FunctionComponent<{
  start: Date;
  end: Date;
  tenant: Tenant;
}> = ({ start, end, tenant }) => {
  const { data } = useData<DailyEnvironmentMetrics[]>(
    () =>
      `/web/tenants/${tenant.id}/usage_stats/${formatDate(start)}/${formatDate(
        end
      )}`
  );

  if (!data) {
    return <Loading />;
  }

  const labels = generateLabels({ start, end });
  let environmentMetrics: { [key: string]: DailyEnvironmentMetrics[] } = {};
  for (let dem of data) {
    environmentMetrics[dem.environment_name] ||= [];
    environmentMetrics[dem.environment_name].push(dem);
  }

  const graphData = {
    labels,
    datasets: Object.entries(environmentMetrics).map(
      ([envName, metrics], id) => ({
        id,
        label: envName,
        data: broadcastDates(
          labels,
          metrics.map((row) => ({
            date: row.date,
            value: row.metric_value,
          }))
        ),
      })
    ),
  };

  const graphOptions = {
    interaction: {
      intersection: false,
      mode: "index" as "index",
    },
    plugins: {
      title: {
        display: true,
        text: "Monthly Active Users (Rolling 30 Days)",
      },
    },
    scales: {
      x: {},
      y: {
        min: 0,
        title: {
          text: "Number of users",
          display: true,
        },
      },
    },
  };

  return <Line data={graphData} options={graphOptions} />;
};

const PerEnvironmentUsageGraph: FunctionComponent<{
  start: Date;
  end: Date;
  env: string;
}> = ({ start, end, env }) => {
  const { data } = useData<DailyEnvironmentMetrics[]>(
    () =>
      `/web/environment/${env}/usage_stats/${formatDate(start)}/${formatDate(
        end
      )}`
  );

  if (!data) {
    return <Loading />;
  }

  const graphOptions = {
    interaction: {
      intersection: false,
      mode: "index" as "index",
    },
    plugins: {
      title: {
        display: true,
        text: "Monthly Active Users (Rolling 30 Days)",
      },
      tooltip: {
        callbacks: {
          footer: (tooltipItems: { parsed: { y: number | null } }[]) => {
            let total = tooltipItems.reduce((a, e) => a + (e.parsed.y || 0), 0);
            return "Total: " + total;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        min: 0,
        title: {
          text: "Number of users",
          display: true,
        },
      },
    },
  };

  const userTypeSet: Set<string> = new Set();
  for (let row of data) {
    for (let userType in row.metric_dimensions) {
      userTypeSet.add(userType);
    }
  }
  const userTypes: string[] = Array.from(userTypeSet);
  userTypes.sort();

  const labels = generateLabels({ start, end });

  const graphData = {
    labels,
    datasets: [
      ...userTypes.map((userType, id) => ({
        id,
        label: userType,
        data: broadcastDates(
          labels,
          data.map((row) => ({
            date: row.date,
            value: row.metric_dimensions[userType],
          }))
        ),
      })),
    ],
  };
  return <Bar data={graphData} options={graphOptions} />;
};

const boundedDate = (dateString: string) => {
  const date = parseISO(dateString);
  return !isValid(date) || date < earliest || date > latest ? null : date;
};

const UsagePage: NextPage = () => {
  Chart.register(...registerables);
  const { tenant } = useTenant();
  const defaultStart = sub(latest, { days: 30 });
  const defaultEnd = latest;
  const [{ start, end }, setRange] = useState({
    start: defaultStart,
    end: defaultEnd,
  });
  const [env, setEnv] = useState<string>(ALL_ENVS);
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <Head>
        <title>Oso Cloud - Usage</title>
      </Head>
      <MainContent>
        {error && (
          <div className="mb-5">
            <div className="border border-error rounded px-3 py-2 text-error">
              <HiOutlineExclamation className="inline mr-2" />
              <span className="align-middle whitespace-pre-line">{error}</span>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-start justify-between">
          <div className="font-space text-3xl">Usage</div>
          <div className="mt-2" />
          <div>
            Billing is determined by your production environment usage. By
            default, this is the environment that has the highest maximum
            Monthly Active Users in a given bill cycle. If you generate large
            amounts of unique users in non-production environments, please reach
            out to us on Slack to make alternate billing arrangements.
          </div>
          <div className="mt-2" />
        </div>
        {tenant ? (
          <div className="mt-2 mb-5">
            <DateRangeComponent
              defaultStart={defaultStart}
              defaultEnd={defaultEnd}
              handleChange={({ start, end }) => {
                const startDate = boundedDate(start);
                const endDate = boundedDate(end);
                if (startDate && endDate) {
                  if (endDate < startDate) {
                    setError("Start date should be before end date");
                  } else if (
                    differenceInDays(endDate, startDate) > MAX_RANGE_DAYS
                  ) {
                    setError(
                      `The date range may not span more than ${MAX_RANGE_DAYS} days`
                    );
                  } else {
                    setError(null);
                    setRange({ start: startDate, end: endDate });
                  }
                }
              }}
            />
            <EnvironmentComponent
              tenant={tenant}
              selectedEnv={env}
              handleChange={(env) => setEnv(env)}
            />
            {env === ALL_ENVS ? (
              <AllEnvironmentsUsageGraph
                start={start}
                end={end}
                tenant={tenant}
              />
            ) : (
              <PerEnvironmentUsageGraph start={start} end={end} env={env} />
            )}
          </div>
        ) : (
          <Loading />
        )}
      </MainContent>
    </>
  );
};

export default UsagePage;
